import { forwardRef, InputHTMLAttributes, useState } from "react";
import { get } from "react-hook-form";
import type { FieldValues, FieldErrors } from "react-hook-form";
import cx from "classnames";
import { useTranslation } from "next-i18next";
import Image from "next/image";

import { Label } from "common/ui/Label";
import { ErrorDisplay } from "common/ui/ErrorDisplay";
import { Icon } from "common/ui/Icon";
import { Stack } from "common/ui/Stack";
import { MoreInfoIcon } from "common/ui/MoreInfoIcon";
import { Paragraph } from "common/ui/Paragraph";

import visibilityOn from "common/assets/icons/visibility-on.svg";
import visibilityOff from "common/assets/icons/visibility-off.svg";

import styles from "./Input.module.scss";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string,
  error?: string,
  errors?: FieldErrors<FieldValues>,
  symbol?: "percent" | "dollar-en" | "dollar-fr" | "numbered",
  light?: boolean,
  helpMessage?: string,
  suffix?: string,
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  name,
  label,
  error,
  errors,
  disabled,
  symbol,
  className,
  light,
  type,
  readOnly,
  helpMessage,
  suffix,
  required,
  ...rest
}, ref) => {
  const { t } = useTranslation("validation");
  const validationErr = get(errors, name);

  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className={cx(styles.Input, className)}>
      {label && (
        <Stack gap={8}>
          <label htmlFor={name}>
            <Label variant="l4" light={light}>
              {label}
              {required && <span className={styles.Required}>*</span>}
            </Label>
          </label>
          {helpMessage && (
            <MoreInfoIcon message={helpMessage} />
          )}
        </Stack>
      )}
      <span className={cx(styles.InputWrapper, {
        [styles.Percent]: symbol === "percent",
        [styles.DollarEn]: symbol === "dollar-en",
        [styles.DollarFr]: symbol === "dollar-fr",
        [styles.Numbered]: symbol === "numbered",
        [styles.PercentReadOnly]: symbol === "percent" && readOnly,
        [styles.DollarFrReadOnly]: symbol === "dollar-fr" && readOnly,
      })}
      >
        <input
          ref={ref}
          className={cx({
            [styles.Error]: error || validationErr,
            [styles.SymbolSuffix]: symbol === "percent" || symbol === "dollar-fr",
            [styles.Prefix]: symbol === "dollar-en" || symbol === "numbered",
            [styles.Password]: type === "password",
            [styles.SuffixReadOnly]: (symbol === "percent" || symbol === "dollar-fr") && readOnly,
          })}
          id={name}
          name={name}
          disabled={disabled}
          type={isVisible ? "text" : type}
          readOnly={readOnly}
          required={required}
          onWheel={(e) => e.currentTarget.blur()}
          {...rest}
        />
        {type === "password" && (
          <Image
            className={styles.VisibilityIcon}
            src={isVisible ? visibilityOn : visibilityOff}
            alt=""
            width={18}
            height={18}
            onClick={() => setIsVisible(!isVisible)}
          />
        )}
        {readOnly && (
          <Icon
            className={styles.LockIcon}
            icon="lock"
          />
        )}
        {suffix && (
          <Paragraph
            className={styles.Suffix}
            variant="p1"
          >
            {suffix}
          </Paragraph>
        )}
      </span>
      {(validationErr || error) && (
        <ErrorDisplay
          error={
            error
            || (validationErr && validationErr.message
              ? t(validationErr.message)
              : t(`${name}.${validationErr?.type}`)
            )
          }
        />
      )}
    </div>
  )
})

Input.displayName = "Input";

